import type { ColumnDef } from '@tanstack/react-table'
import type { IProvince } from '@/entities/country'
import type { PaginationConfig } from '@/shared/ui/data-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LocationBannerCell,
  LocationBannerEditButton,
} from '@/features/location/location-banner-cell'
import { LocationStatusBadge } from '@/features/location/location-status-badge'
import { Badge } from '@/shared/ui/badge'
import { DataTable } from '@/shared/ui/data-table'

interface ProvinceListTableProps {
  provinces: IProvince[]
  loading: boolean
  pagination: PaginationConfig
  onEditBanner: (province: IProvince) => void
  onPreviewBanner: (url: string) => void
}

/**
 * Province list of the selected country, with a per-row banner editor.
 */
export function ProvinceListTable({
  provinces,
  loading,
  pagination,
  onEditBanner,
  onPreviewBanner,
}: ProvinceListTableProps) {
  const { t } = useTranslation()

  const columns = useMemo<ColumnDef<IProvince>[]>(
    () => [
      {
        id: 'banner',
        header: t('location.banner'),
        cell: ({ row }) => (
          <LocationBannerCell banner={row.original.banner} onPreview={onPreviewBanner} />
        ),
      },
      { accessorKey: 'code', header: t('location.code') },
      { accessorKey: 'nativeName', header: t('location.nativeName') },
      {
        id: 'name',
        header: t('location.englishName'),
        cell: ({ row }) => row.original.name ?? '—',
      },
      {
        id: 'type',
        header: t('location.type'),
        cell: ({ row }) => (
          <Badge variant="outline" className="border-zinc-200 bg-zinc-50 text-zinc-600">
            {t(`location.provinceType.${row.original.type}`, { defaultValue: row.original.type })}
          </Badge>
        ),
      },
      {
        id: 'status',
        header: () => <div className="text-center">{t('common.status')}</div>,
        cell: ({ row }) => <LocationStatusBadge isActive={row.original.isActive} />,
      },
      {
        id: 'actions',
        size: 64,
        header: () => null,
        cell: ({ row }) => <LocationBannerEditButton onClick={() => onEditBanner(row.original)} />,
      },
    ],
    [onEditBanner, onPreviewBanner, t],
  )

  return (
    <DataTable
      columns={columns}
      data={provinces}
      loading={loading}
      pagination={pagination}
      emptyText={t('common.noData')}
    />
  )
}
